class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.mobileSuccessBanner = document.getElementById('mobile-cart-success-banner');
    this.mobileCartOverlay = document.getElementById('mobile-cart-overlay');
    this.mobileCartOverlayCount = document.getElementById('mobile-cart-overlay-count');
    this.isMobile = window.innerWidth < 750;
    this.cartUpdateUnsubscriber = undefined;

    // Handle window resize to update mobile detection
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 750;
    });

    // Ensure success banner is hidden on page load/refresh
    if (this.mobileSuccessBanner) {
      this.mobileSuccessBanner.setAttribute('aria-hidden', 'true');
    }

    // Ensure overlay is hidden on page load until we check cart state
    if (this.mobileCartOverlay) {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
    }

    if (this.notification) {
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
        closeButton.addEventListener('click', this.close.bind(this))
      );
    }

    // Subscribe to cart updates to keep mobile overlay in sync
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Always update mobile cart overlay when cart updates
        // If cartData is provided, use it; otherwise fetch fresh data
        if (event.cartData) {
          this.updateMobileCartOverlay(event.cartData);
        } else {
          // Fetch fresh cart data if not provided in event
          this.initializeMobileCartOverlay();
        }
      });
    }

    // Initialize mobile cart overlay on page load (with delay to prevent flash)
    setTimeout(() => {
      this.initializeMobileCartOverlay();
    }, 100);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async initializeMobileCartOverlay() {
    // Check if we're on the cart page - don't show overlay on cart page
    const isCartPage = window.location.pathname === routes.cart_url || 
                       window.location.pathname.includes('/cart') ||
                       document.body.classList.contains('template-cart') ||
                       window.location.pathname.includes('/ai-skin-assessment');
    
    // Check if we're on product page (PDP) or collection page (PLP)
    const isProductPage = document.body.classList.contains('template-product') ||
                          window.location.pathname.includes('/products/');
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                             window.location.pathname.includes('/collections/');
    const isHomePage = document.body.classList.contains('template-index') ||
                       window.location.pathname === '/' ||
                       window.location.pathname === '';
    const isPDPOrPLPOrHome = isProductPage || isCollectionPage || isHomePage;
    
    // Hide overlay if not on PDP/PLP or on cart page
    if (isCartPage || !isPDPOrPLPOrHome) {
      if (this.mobileCartOverlay) {
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    
    // Fetch current cart state to initialize overlay
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (response.ok) {
        const cartData = await response.json();
        // Only update if cart has items, otherwise keep it hidden
        if (cartData.item_count > 0) {
          this.updateMobileCartOverlay(cartData);
        } else {
          // Ensure overlay is hidden if cart is empty
          if (this.mobileCartOverlay) {
            this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      // Hide overlay on error
      if (this.mobileCartOverlay) {
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
      }
    }
  }

  open() {
    if (!this.notification) return;
    
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;
    
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  showMobileSuccessBanner() {
    if (!this.mobileSuccessBanner) return;
    
    this.mobileSuccessBanner.setAttribute('aria-hidden', 'false');
    
    // Auto-hide after 3 seconds
    clearTimeout(this.mobileBannerTimeout);
    this.mobileBannerTimeout = setTimeout(() => {
      this.hideMobileSuccessBanner();
    }, 3000);
  }

  hideMobileSuccessBanner() {
    if (!this.mobileSuccessBanner) return;
    this.mobileSuccessBanner.setAttribute('aria-hidden', 'true');
  }

  async updateMobileCartOverlay(cartData) {
    // Re-fetch elements in case they weren't available during construction
    if (!this.mobileCartOverlay) {
      this.mobileCartOverlay = document.getElementById('mobile-cart-overlay');
    }
    if (!this.mobileCartOverlayCount) {
      this.mobileCartOverlayCount = document.getElementById('mobile-cart-overlay-count');
    }
    
    if (!this.mobileCartOverlay || !this.mobileCartOverlayCount) {
      return;
    }
    
    // Fetch full cart data if not provided or incomplete
    let fullCartData = cartData;
    if (!cartData?.item_count && (!cartData?.items || cartData.items.length === 0)) {
      try {
        const response = await fetch(`${routes.cart_url}.js`);
        if (response.ok) {
          fullCartData = await response.json();
        }
      } catch (error) {
        console.error('Error fetching cart data:', error);
        // Hide overlay if fetch fails
        this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
        return;
      }
    }
    
    const itemCount = fullCartData?.item_count || 0;
    const itemText = itemCount === 1 ? 'Item' : 'Items';
    
    this.mobileCartOverlayCount.textContent = `${itemCount} ${itemText}`;
    
    // Update product images
    await this.updateMobileCartImages(fullCartData);
    
    // Check if we're on the cart page
    const isCartPage = window.location.pathname === routes.cart_url || 
                       window.location.pathname.includes('/cart') ||
                       document.body.classList.contains('template-cart');
    
    // Check if we're on product page (PDP) or collection page (PLP)
    const isProductPage = document.body.classList.contains('template-product') ||
                          window.location.pathname.includes('/products/');
    const isCollectionPage = document.body.classList.contains('template-collection') ||
                             window.location.pathname.includes('/collections/');
    const isHomePage = document.body.classList.contains('template-index') ||
                       window.location.pathname === '/' ||
                       window.location.pathname === '';
    const isPDPOrPLPOrHome = isProductPage || isCollectionPage || isHomePage;
    
    // Show overlay ONLY if there are items in cart, on mobile, NOT on cart page, and on PDP/PLP
    // Hide immediately if cart is empty to prevent flashing

    if (itemCount > 0 && this.isMobile && !isCartPage && isPDPOrPLPOrHome) {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'false');
    } else {
      this.mobileCartOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  async updateMobileCartImages(cartData) {
    const imagesContainer = document.getElementById('mobile-cart-overlay-images');
    if (!imagesContainer) return;

    let items = cartData?.items || [];
    
    // If items don't have image data, fetch from cart.js
    if (items.length > 0 && !items[0].image && !items[0].featured_image) {
      try {
        const response = await fetch(`${routes.cart_url}.js`);
        if (response.ok) {
          const fullCartData = await response.json();
          items = fullCartData.items || items;
        }
      } catch (error) {
        console.error('Error fetching cart data for images:', error);
      }
    }
    
    // Get last 2 items - newest items are at the end of the array
    // Reverse to show newest first (most recently added on top)
    const recentItems = items.slice(0, 2);
    
    if (recentItems.length === 0) {
      imagesContainer.innerHTML = '';
      return;
    }
    
    // Get the newest item (first in reversed array) - this is the LAST item added
    const newestItem = recentItems[0];
    const newestItemId = newestItem?.variant_id || newestItem?.id || newestItem?.key;
    
    // Check if we have existing images to animate
    const existingImages = imagesContainer.querySelectorAll('.mobile-cart-overlay__image-wrapper');
    const hasExistingImages = existingImages.length > 0;
    
    // Try to detect if a new product was added by comparing variant/product IDs
    let shouldAnimate = false;
    if (hasExistingImages && newestItemId) {
      const currentFirstImage = existingImages[0];
      const currentFirstImageDataId = currentFirstImage?.getAttribute('data-variant-id');
      
      // If the newest item ID is different from current first image, animate
      if (currentFirstImageDataId && currentFirstImageDataId !== String(newestItemId)) {
        shouldAnimate = true;
      } else if (!currentFirstImageDataId) {
        // If no data attribute, try to compare by image URL
        const currentFirstImageSrc = currentFirstImage?.querySelector('img')?.src || '';
        const newestItemImageUrl = this.getImageUrl(newestItem);
        if (newestItemImageUrl) {
          // Extract filename from URLs for comparison
          const currentFilename = currentFirstImageSrc.split('/').pop().split('?')[0];
          const newestFilename = newestItemImageUrl.split('/').pop().split('?')[0];
          if (currentFilename !== newestFilename) {
            shouldAnimate = true;
          }
        }
      }
    }
    
    // If we should animate and have existing images
    if (shouldAnimate && hasExistingImages && newestItem) {
      const currentFirstImage = existingImages[0];
      
      // Animate: move current first to second position
      if (currentFirstImage) {
        currentFirstImage.classList.add('animate-to-second');
      }
      
      // Remove old second image if it exists
      if (existingImages[1]) {
        existingImages[1].style.opacity = '0';
        setTimeout(() => {
          if (existingImages[1].parentNode) {
            existingImages[1].remove();
          }
        }, 400);
      }
      
      // Create and add new first image with drop-in animation
      const newFirstImage = this.createImageElement(newestItem, 0, recentItems.length);
      if (newFirstImage) {
        newFirstImage.setAttribute('data-variant-id', String(newestItemId));
        
        // Set initial position for drop-in animation (from top)
        newFirstImage.style.transform = 'translate(0, -20rem) scale(0.4)';
        newFirstImage.style.opacity = '0';
        newFirstImage.style.transition = 'none';
        
        imagesContainer.insertBefore(newFirstImage, currentFirstImage);
        
        // Trigger drop-in animation using requestAnimationFrame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            newFirstImage.style.transition = 'transform 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55), opacity 0.7s ease, scale 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55)';
            newFirstImage.style.transform = 'translate(0, 0) scale(1)';
            newFirstImage.style.opacity = '1';
            
            // Add bounce effect
            setTimeout(() => {
              newFirstImage.style.transition = 'transform 0.2s ease';
              newFirstImage.style.transform = 'translate(0, 0.3rem) scale(1.05)';
              setTimeout(() => {
                newFirstImage.style.transform = 'translate(0, -0.15rem) scale(0.98)';
                setTimeout(() => {
                  newFirstImage.style.transform = 'translate(0, 0) scale(1)';
                }, 100);
              }, 100);
            }, 400);
          });
        });
        
        // Clean up animation classes after animation completes
        setTimeout(() => {
          if (currentFirstImage) {
            currentFirstImage.classList.remove('animate-to-second');
          }
          newFirstImage.style.transition = '';
        }, 1000);
      }
      
      return;
    }
    
    // If no animation needed, just update normally - ensure newest is always first
    imagesContainer.innerHTML = '';
    
    // Create image elements - first item (index 0) is the newest and should appear on top
    recentItems.forEach((item, index) => {
      const imgWrapper = this.createImageElement(item, index, recentItems.length);
      if (imgWrapper) {
        const itemId = item?.variant_id || item?.id || item?.key;
        if (itemId) {
          imgWrapper.setAttribute('data-variant-id', String(itemId));
        }
        // Ensure first item (newest) has highest z-index
        if (index === 0) {
          imgWrapper.style.zIndex = '10';
        } else {
          imgWrapper.style.zIndex = '5';
        }
        imagesContainer.appendChild(imgWrapper);
      }
    });
  }

  getImageUrl(item) {
    if (item.image) {
      if (typeof item.image === 'string' && item.image.startsWith('http')) {
        return item.image;
      } else if (item.image.url) {
        return item.image.url;
      } else if (item.featured_image) {
        return item.featured_image;
      }
    } else if (item.featured_image) {
      return item.featured_image;
    }
    return null;
  }

  createImageElement(item, index, totalItems) {
    const imageUrl = this.getImageUrl(item);
    if (!imageUrl) return null;

    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'mobile-cart-overlay__image-wrapper';
    imgWrapper.style.zIndex = totalItems - index;
    
    const img = document.createElement('img');
    if (imageUrl.includes('cdn.shopifycdn.com') || imageUrl.includes('cdn.shopify.com')) {
      img.src = imageUrl.replace(/(\.(jpg|jpeg|png|webp))/i, '_100x100$1');
    } else {
      img.src = imageUrl;
    }
    img.alt = item.product_title || item.title || 'Product';
    img.className = 'mobile-cart-overlay__image';
    img.loading = 'lazy';
    
    img.onerror = function() {
      if (this.src !== imageUrl) {
        this.src = imageUrl;
      }
    };
    
    imgWrapper.appendChild(img);
    return imgWrapper;
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    // Update mobile cart overlay with cart data (async, don't wait)
    this.updateMobileCartOverlay(parsedState).catch(err => {
      console.error('Error updating mobile cart overlay:', err);
    });

    // On mobile, show success banner and bottom overlay instead of desktop notification
    // Only show success banner when a product is actually added (not on page load)
    if (this.isMobile) {
      // Show success banner only when renderContents is called (product was added)
      this.showMobileSuccessBanner();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-section',
        section: 'cart-section',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
